import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import '../report.css';
import ApiService from '../../../../services/apiService';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const intFmt = (n) =>
  new Intl.NumberFormat('en-GB', {
    maximumFractionDigits: 0,
  }).format(Number.isFinite(+n) ? +n : 0);

const dateToInputValue = (d) => new Date(d).toISOString().slice(0, 10);

const COLUMNS = [
  { key: 'itemNo', label: 'Item No' },
  { key: 'itemDescription', label: 'Item Description' },
  { key: 'soldQty', label: 'Sold Qty', align: 'right' },
];

const UnSoldItemReport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalRecords: 0,
    totalPages: 0,
  });

  const now = new Date();
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(now.getDate() - 10);

  const [formData, setFormData] = useState({
    fromDate: dateToInputValue(tenDaysAgo),
    toDate: dateToInputValue(now),
  });

  const [searchText, setSearchText] = useState('');
  const [sortState, setSortState] = useState({
    key: 'itemDescription',
    dir: 'asc',
  });

  const pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
  ];

  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');
      const list = res?.data?.result || [];

      setBranches(
        list.map((b) => ({
          value: b.id,
          label: b.branchName,
        })),
      );
    } catch (error) {
      console.error('Branch Load Error:', error);
    }
  };

  const fetchData = async (page = 1, size = pagination.pageSize) => {
    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      params.append('PageNo', page);
      params.append('PageSize', size);
      params.append('StartDate', formData.fromDate);
      params.append('EndDate', formData.toDate);
      params.append('BranchId', selectedBranch?.value || 0);

      const res = await apiService.getApiParamater('GET_UNSOLD_ITEMS', `?${params.toString()}`);

      const result = Array.isArray(res?.data?.result) ? res.data.result : [];
      const pg = res?.data?.paginationData || {};

      const totalRecords = pg.totalRecords ?? 0;
      const currentPage = pg.currentPage ?? page;
      const pageSize = pg.pageSize ?? size;
      const totalPages = pg.totalPages ?? (totalRecords ? Math.ceil(totalRecords / pageSize) : 0);

      setRows(result);
      setPagination({
        currentPage,
        pageSize,
        totalRecords,
        totalPages,
      });
    } catch (error) {
      console.error(error);
      setErr('Failed to fetch unsold report. Please try again.');
      setRows([]);
      setPagination((p) => ({
        ...p,
        totalRecords: 0,
        totalPages: 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRows = useMemo(() => {
    let data = [...rows];

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      data = data.filter(
        (x) =>
          String(x.itemNo || '')
            .toLowerCase()
            .includes(q) ||
          String(x.itemDescription || '')
            .toLowerCase()
            .includes(q),
      );
    }

    return data;
  }, [rows, searchText]);

  const sortedRows = useMemo(() => {
    const { key, dir } = sortState;
    const copy = [...filteredRows];

    copy.sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];

      const na = Number(av);
      const nb = Number(bv);

      if (Number.isFinite(na) && Number.isFinite(nb)) {
        return dir === 'asc' ? na - nb : nb - na;
      }

      return dir === 'asc' ? String(av ?? '').localeCompare(String(bv ?? '')) : String(bv ?? '').localeCompare(String(av ?? ''));
    });

    return copy;
  }, [filteredRows, sortState]);

  const totals = useMemo(() => {
    const totalUnsoldItems = sortedRows.length;
    const totalSoldQty = sortedRows.reduce((sum, r) => sum + (+r.soldQty || 0), 0);

    return { totalUnsoldItems, totalSoldQty };
  }, [sortedRows]);

  const onChangeRange = (e) => setFormData((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSearch = () => fetchData(1, pagination.pageSize);

  const onPageSize = (opt) => {
    const size = opt?.value || 20;
    setPagination((p) => ({ ...p, pageSize: size, currentPage: 1 }));
    fetchData(1, size);
  };

  const onSort = (key) => {
    setSortState((prev) => (!prev || prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const exportCSV = () => {
    const headers = COLUMNS.map((c) => `"${c.label}"`).join(',');
    const body = sortedRows.map((r) => COLUMNS.map((c) => `"${String(r?.[c.key] ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');

    const csv = `${headers}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unsold-items-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    if (!sortedRows.length) return;

    const header = COLUMNS.map((c) => c.label);

    const body = sortedRows.map((r) => [r.itemNo || '', r.itemDescription || '', Number(r.soldQty) || 0]);

    const footer1 = ['Total Unsold Items', '', totals.totalUnsoldItems];
    const footer2 = ['Total Sold Qty', '', totals.totalSoldQty];

    const aoa = [header, ...body, [], footer1, footer2];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws['!cols'] = [{ wch: 18 }, { wch: 40 }, { wch: 12 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Unsold Items');

    const branchName = selectedBranch?.label ? selectedBranch.label.replace(/[\\/:*?"<>|]/g, '-') : 'All-Branches';

    XLSX.writeFile(wb, `unsold-items_${branchName}_${formData.fromDate}_to_${formData.toDate}.xlsx`);
  };

  return (
    <div className="p-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      <div className="row g-3 align-items-end">
        <div className="col-12 col-md-3">
          <label className="form-label">Select Branch</label>
          <Select
            value={selectedBranch}
            onChange={setSelectedBranch}
            options={branches}
            placeholder="Select Branch"
            isClearable
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
          />
        </div>

        <div className="col-12 col-md-3">
          <label className="form-label">
            {t('From')} {t('Date')}
          </label>
          <input type="date" className="form-control" name="fromDate" value={formData.fromDate} onChange={onChangeRange} />
        </div>

        <div className="col-12 col-md-3">
          <label className="form-label">
            {t('To')} {t('Date')}
          </label>
          <input type="date" className="form-control" name="toDate" value={formData.toDate} onChange={onChangeRange} />
        </div>

        <div className="col-12 col-md-3">
          <label className="form-label">Search Item</label>
          <input
            type="text"
            className="form-control"
            placeholder="Search by item no or description"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <div className="col d-flex gap-2 mt-4 mb-4">
          <button className="addItem-btn col-2" onClick={onSearch} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>

          <Select
            className="ms-auto"
            value={pageSizeOptions.find((o) => o.value === pagination.pageSize)}
            onChange={onPageSize}
            options={pageSizeOptions}
            isSearchable={false}
          />

          <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={!sortedRows.length}>
            Export CSV
          </button>

          <button className="btn btn-outline-success" onClick={exportExcel} disabled={!sortedRows.length}>
            Export Excel
          </button>
        </div>
      </div>

      <div className="POsgridshad rounded p-0 mt-4">
        {err && <div className="alert alert-danger m-3">{err}</div>}

        <div className="custom-table-container">
          <table className="custom-table">
            <thead style={{ position: 'sticky', top: 0, zIndex: 0 }}>
              <tr>
                {COLUMNS.map((c) => {
                  const sorted = sortState?.key === c.key;
                  return (
                    <th
                      key={c.key}
                      onClick={() => onSort(c.key)}
                      className={`${c.align === 'right' ? 'text-end' : ''} ${sorted ? 'table-active' : ''}`}
                      style={{ cursor: 'pointer' }}
                    >
                      {c.label}
                      {sorted ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center py-4">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && sortedRows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center text-muted py-4">
                    No data found
                  </td>
                </tr>
              )}

              {!loading &&
                sortedRows.map((r) => (
                  <tr key={r.recordId}>
                    <td>{r.itemNo || '—'}</td>
                    <td>{r.itemDescription || '—'}</td>
                    <td className="text-end">{intFmt(r.soldQty)}</td>
                  </tr>
                ))}
            </tbody>

            {sortedRows.length > 0 && (
              <tfoot>
                <tr className="bg-light fw-bold">
                  <td colSpan={2}>Total Unsold Items</td>
                  <td className="text-end">{intFmt(totals.totalUnsoldItems)}</td>
                </tr>
                <tr className="fw-bold">
                  <td colSpan={2}>Total Sold Qty</td>
                  <td className="text-end">{intFmt(totals.totalSoldQty)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

const mapStatetoProps = (state) => state;
export default connect(mapStatetoProps)(UnSoldItemReport);
